import React, { useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import LazyFieldListFolder from "./LazyFieldListFolder";
import FieldListEntry from "./FieldListEntry";
import { IAppState } from "../../types/stores/index";
import { IField } from "../../types/stores/fieldTypes";
import { IFieldsStore } from "../../types/stores/fields";
import { FieldVisibilityTypes } from "../../helpers/constants";
import { createDeepEqualSelector } from "../../helpers/typedHelpers";
import {
    makeGetLazyMisalignment,
    makeLazyTabFilterSelector,
    alignedWithParent,
    get3PMShortNames,
    getCtrlShiftSelectionRange,
    shouldGrayoutField,
    applyFieldVisibilityFilter,
    selectIsSearchActive,
    sortChildIds,
} from "../../helpers/lazyLoadingHelper";
import { setVar } from "../../actions/actionCreators";
import { setSelectedFields } from "../../actions/fieldTreeActions";
import { getFlowFiltersForSelectedFlow } from "../../reducers/flowFilters";

const TAB_MISALIGNED = 3;

type Props = {
    fieldKey: string | number;
    treeViewOnUpdate: () => void;
    TreeType: number;
    isPIIOnly: boolean;
    editable: boolean;
    showHiddenFields: boolean;
    ctrlShift: boolean;
    showAllDuringSearch: boolean;
    isModal: boolean;
    showTabs: boolean;
    tabType: number;
};

interface IChildrenIDStruct {
    folderIds: Array<string | number>;
    fieldIds: Array<string | number>;
}

/**
 * Memoized selector factory: Gets the appropriate field store based on TreeType
 * - EXPORT: Uses layoutfields store
 * - REPORT: Uses reportFields store
 * - SELECT (default): Uses fields store
 */
const makeGetFields = () =>
    createDeepEqualSelector(
        (state: IAppState, props: { TreeType: number }) => {
            let fields: IFieldsStore;

            if (props.TreeType == FieldVisibilityTypes.EXPORT) {
                fields = state.layoutfields;
            } else if (props.TreeType == FieldVisibilityTypes.REPORT) {
                fields = state.reportFields;
            } else {
                fields = state.fields;
            }

            return fields;
        },
        (fields: IFieldsStore) => fields
    );

/**
 * Memoized selector factory: Gets sorted child IDs (folders and fields) for a parent folder
 */
const makeGetChildIds = () =>
    createDeepEqualSelector(
        (state: IAppState, props: { TreeType: number; fieldKey: string | number }) => {
            switch (props.TreeType) {
                case FieldVisibilityTypes.EXPORT:
                    return state.layoutfields;
                case FieldVisibilityTypes.REPORT:
                    return state.reportFields;
                default:
                    return state.fields;
            }
        },
        // Text search maps
        (state: IAppState) => state.vars.searchResultsByParent,
        // Field label search maps
        (state: IAppState) => state.vars.fieldLabelSearchResultsByParent,
        // Umbrella label search maps
        (state: IAppState) => state.vars.umbrellaLabelSearchResultsByParent,
        // Search active boolean
        selectIsSearchActive,
        (_, props: { fieldKey: string | number }) => props.fieldKey,
        (_, props: { isPIIOnly: boolean }) => props.isPIIOnly,
        // Get lookup dictionary for sorting
        (state: IAppState, props: { TreeType: number }) => {
            const isSearching = selectIsSearchActive(state);
            const hasFieldLabelSearch =
                state.vars.fieldLabelSearchResults && Object.keys(state.vars.fieldLabelSearchResults).length > 0;
            const hasUmbrellaLabelSearch =
                state.vars.umbrellaLabelSearchResults && Object.keys(state.vars.umbrellaLabelSearchResults).length > 0;

            if (isSearching && state.vars.searchResults) {
                return state.vars.searchResults;
            }
            if (hasFieldLabelSearch && state.vars.fieldLabelSearchResults) {
                return state.vars.fieldLabelSearchResults;
            }
            if (hasUmbrellaLabelSearch && state.vars.umbrellaLabelSearchResults) {
                return state.vars.umbrellaLabelSearchResults;
            }

            // Otherwise use byFolder from appropriate store
            switch (props.TreeType) {
                case FieldVisibilityTypes.EXPORT:
                    return state.layoutfields.byFolder;
                case FieldVisibilityTypes.REPORT:
                    return state.reportFields.byFolder;
                default:
                    return state.fields.byFolder;
            }
        },
        (
            fieldsStore,
            searchResultsByParent,
            fieldLabelSearchResultsByParent,
            umbrellaLabelSearchResultsByParent,
            isSearching,
            fieldKey,
            isPIIOnly,
            lookup
        ): IChildrenIDStruct => {
            // Determine which parent map to use
            let byParent;
            const hasFieldLabelSearch =
                fieldLabelSearchResultsByParent && Object.keys(fieldLabelSearchResultsByParent).length > 0;
            const hasUmbrellaLabelSearch =
                umbrellaLabelSearchResultsByParent && Object.keys(umbrellaLabelSearchResultsByParent).length > 0;

            if (isSearching && searchResultsByParent) {
                byParent = searchResultsByParent;
            } else if (hasFieldLabelSearch) {
                byParent = fieldLabelSearchResultsByParent;
            } else if (hasUmbrellaLabelSearch) {
                byParent = umbrellaLabelSearchResultsByParent;
            } else {
                byParent = fieldsStore.byParent;
            }

            let ids = byParent?.[fieldKey] ?? [];
            let validIds = ids.filter(id => lookup[id] != null);
            if (validIds.length === 0) {
                return { folderIds: [], fieldIds: [] };
            }

            if (isPIIOnly) {
                let piiFields: Array<IField> = [];
                Object.keys(lookup).forEach(x => {
                    const field: IField = lookup[x];
                    if (field.IsPII) {
                        piiFields.push(field);
                    }
                });
                validIds = piiFields.map(x => x.workid!.toString());
            }
            const sortedIds = sortChildIds([...validIds], lookup);
            const folderIds = sortedIds.filter(x => lookup[x] && !lookup[x].isLeaf);
            const fieldIds = sortedIds.filter(x => lookup[x] && lookup[x].isLeaf);
            return { folderIds, fieldIds };
        }
    );

/**
 * Memoized selector factory: Gets the appropriate lookup dictionary
 */
const makeLookupSelector = () =>
    createDeepEqualSelector(
        (state: IAppState) => state.vars.searchResults,
        (state: IAppState) => state.vars.fieldLabelSearchResults,
        (state: IAppState) => state.vars.umbrellaLabelSearchResults,
        selectIsSearchActive,
        (_: IAppState, props: { fields: IFieldsStore }) => props.fields.byFolder,
        (searchResults, fieldLabelSearchResults, umbrellaLabelSearchResults, isSearching, normalByFolder) => {
            const hasFieldLabelSearch = fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0;
            const hasUmbrellaLabelSearch =
                umbrellaLabelSearchResults && Object.keys(umbrellaLabelSearchResults).length > 0;

            if (isSearching && searchResults) {
                return searchResults;
            }
            if (hasFieldLabelSearch) {
                return fieldLabelSearchResults;
            }
            if (hasUmbrellaLabelSearch) {
                return umbrellaLabelSearchResults;
            }
            return normalByFolder;
        }
    );

/**
 * LazyFieldList Component
 *
 * Renders a list of child folders and fields for a given parent folder using lazy loading.
 * Children are only loaded when the parent folder is expanded.
 */
const LazyFieldList: React.FC<Props> = props => {
    const {
        fieldKey,
        TreeType,
        treeViewOnUpdate,
        showAllDuringSearch,
        editable,
        showHiddenFields,
        ctrlShift,
        isModal,
        showTabs,
        tabType,
    } = props;
    const dispatch = useDispatch();

    // Get session state (feature flags, third-party settings, classification tables)
    const sessionState = useSelector(
        (state: IAppState) => ({
            enabledFeatures: state.session.enabledFeatures,
            thirdPartyShortNames: state.session.thirdPartyShortNames,
            classTables: state.session.fieldClassificationTables,
        }),
        shallowEqual
    );

    const getFields = makeGetFields();
    const fields: IFieldsStore = useSelector((state: IAppState) => getFields(state, props));

    const getChildIds = makeGetChildIds();
    const childIds: IChildrenIDStruct = useSelector((state: IAppState) => getChildIds(state, props));

    const getLookup = makeLookupSelector();
    const lookup = useSelector((state: IAppState) => getLookup(state, { ...props, fields }));
    const isSearchActive = useSelector(selectIsSearchActive);

    const getLazyMisaligned = React.useMemo(() => makeGetLazyMisalignment(), []);
    const { misalignedMap, orphanIds } = useSelector((state: IAppState) => getLazyMisaligned(state));

    const getTabFilter = React.useMemo(() => makeLazyTabFilterSelector(), []);
    const reduxState = useSelector((state: IAppState) => state);

    const selectedFields = useSelector((state: IAppState) => state.selected.selectedFields);

    const flowFilters = useSelector((state: IAppState) => state.flowFilters);
    const flowFiltersInFlow = useSelector((state: IAppState) => getFlowFiltersForSelectedFlow(state));

    const fieldVisibilityFilter = useSelector((state: IAppState) => state.adminDesigner.fieldVisibilityFilter);
    const enabledFieldVisibilityTypes = useSelector(
        (state: IAppState) => state.fieldsByCompany.enabledFieldVisibilityTypes
    );

    // Get field label search results from Redux
    const fieldLabelSearchResults = useSelector((state: IAppState) => state.vars.fieldLabelSearchResults);

    // Get umbrella label search results from Redux
    const umbrellaLabelSearchResults = useSelector((state: IAppState) => state.vars.umbrellaLabelSearchResults);

    // Check if this folder has been loaded
    const loadedFolders = useSelector((state: IAppState) => state.vars.loadedFolders || {});
    const isFolderLoaded = loadedFolders[fieldKey] === true;

    // Check if currently loading
    const isLoading = useSelector((state: IAppState) => {
        const loadingFolders = state.vars?.loadingFolders ?? {};
        return loadingFolders[fieldKey] === true;
    });

    // Update tree view stripes when feature flags change
    useEffect(() => {
        if (treeViewOnUpdate) {
            treeViewOnUpdate();
        }
    }, [sessionState.enabledFeatures, treeViewOnUpdate]);

    // Handler: Ctrl/Shift multi-select
    const onCtrlShift = (lastSelected: number, newSelected: number): void => {
        const fieldsToAdd = getCtrlShiftSelectionRange(
            lastSelected,
            newSelected,
            childIds.fieldIds,
            lookup,
            selectedFields
        );

        // Dispatch all selections
        fieldsToAdd.forEach(fieldKey => {
            dispatch(setSelectedFields(fieldKey));
        });
    };

    // Effect: Auto-switch to "Other" tab when misalignment is detected
    // Only runs in edit mode with tabs enabled
    // Checks loaded fields for classification misalignment with their parent folder.
    // If found, automatically switches to tab 3 (Other) to show misaligned fields.
    useEffect(() => {
        if (!showTabs || !editable || tabType === TAB_MISALIGNED) {
            return; // Only check during edit mode with tabs enabled, and not already on tab 3
        }

        const thirdPartyMPs = get3PMShortNames(sessionState.classTables);

        // Check only fields in state.fields.byFolder (already loaded)
        const loadedFields = fields?.byId || {};
        let hasMisalignment = false;

        // Check each loaded field for misalignment
        for (const workid in loadedFields) {
            const field = loadedFields[workid];
            if (!field || !field.parent) continue;

            const parent = loadedFields[field.parent];
            if (!parent) continue; // Parent not loaded yet, skip

            const isAligned = alignedWithParent(field, parent, thirdPartyMPs);
            if (!isAligned) {
                hasMisalignment = true;
                break;
            }
        }

        // If misalignment found, switch to tab 3
        if (hasMisalignment) {
            dispatch(setVar("selectedFieldTreeTab", TAB_MISALIGNED));
        }
    }, [fields?.byId, fields?.byFolder, showTabs, editable, tabType, sessionState.classTables, dispatch]);

    if (!fields || !fields.byFolder) {
        return null;
    }

    const { folderIds, fieldIds } = childIds;

    // Combine for tab filtering if needed
    const allChildIds = [...folderIds, ...fieldIds];

    let finalFolderIds = folderIds;
    let finalFieldIds = fieldIds;

    // Apply tab filtering if feature is enabled
    if (sessionState.enabledFeatures.includes("field-tree-tabs") && showTabs) {
        const result = getTabFilter(reduxState, {
            tabType,
            showTabs,
            fieldKey,
            lookup,
            childIds: allChildIds,
            misalignedMap,
            orphanIds,
        });

        finalFolderIds = result.folderIds;
        finalFieldIds = result.fieldIds;
    } else if (!editable) {
        // Marketplace feature is off, exclude third-party data in view mode
        finalFolderIds = folderIds.filter(id => lookup[id] && !lookup[id].IsThirdParty);
        finalFieldIds = fieldIds.filter(id => lookup[id] && !lookup[id].IsThirdParty);
    }

    // Apply field label filter using Redux state
    if (fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0) {
        finalFolderIds = folderIds.filter(id => {
            const field = fieldLabelSearchResults[id];
            const result = field && !field.isLeaf;
            return result;
        });

        finalFieldIds = fieldIds.filter(id => {
            const field = fieldLabelSearchResults[id];
            const result = field && field.isLeaf;
            return result;
        });
    }

    // Apply umbrella label filter using Redux state
    if (umbrellaLabelSearchResults && Object.keys(umbrellaLabelSearchResults).length > 0) {
        // If field label search is also active, intersect results
        if (fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0) {
            // Both searches active - intersect
            finalFieldIds = finalFieldIds.filter(id => {
                const field = umbrellaLabelSearchResults[id];
                return field && field.isLeaf;
            });

            finalFolderIds = finalFolderIds.filter(id => {
                const field = umbrellaLabelSearchResults[id];
                return field && !field.isLeaf;
            });
        } else {
            // Only umbrella search active
            finalFieldIds = fieldIds.filter(id => {
                const field = umbrellaLabelSearchResults[id];
                return field && field.isLeaf;
            });

            finalFolderIds = folderIds.filter(id => {
                const field = umbrellaLabelSearchResults[id];
                return field && !field.isLeaf;
            });
        }
    }

    // Apply field visibility filter (Admin mode only)
    if (editable && fieldVisibilityFilter && enabledFieldVisibilityTypes) {
        finalFieldIds = applyFieldVisibilityFilter(
            finalFieldIds,
            lookup,
            fieldVisibilityFilter,
            enabledFieldVisibilityTypes
        );
    }

    // Show "(empty)" if folder is loaded but has no children
    if (isFolderLoaded && !isLoading && finalFolderIds.length + finalFieldIds.length === 0) {
        return <span>(empty)</span>;
    }
    const folders = finalFolderIds;
    const leafFields = finalFieldIds;

    // Render folder elements
    const folderElements = folders.map(id => (
        <LazyFieldListFolder
            key={id}
            fieldKey={id}
            TreeType={TreeType}
            showHiddenFields={showHiddenFields}
            showAllDuringSearch={showAllDuringSearch}
            treeViewOnUpdate={treeViewOnUpdate}
            editable={editable}
            ctrlShift={ctrlShift}
            isModal={isModal}
            showTabs={showTabs}
            tabType={tabType}
        />
    ));

    // Render field elements - Each field is a clickable/selectable entry
    const fieldElements = leafFields.map(id => {
        const f = lookup[id];

        if (!f || !f.fieldkey) {
            return null;
        }

        // Determine if field should have ctrlShift enabled
        // Disable for third-party fields using the same helper as FieldList
        const fieldFollowsThirdPartyRules =
            f.FieldClassification !== null &&
            f.FieldClassification !== "U" &&
            sessionState.thirdPartyShortNames &&
            sessionState.thirdPartyShortNames.includes(f.FieldClassification);

        // Determine if field should be grayed out
        const isGrayout = shouldGrayoutField(
            f,
            lookup,
            flowFilters.byId,
            flowFiltersInFlow,
            sessionState.thirdPartyShortNames,
            editable
        );

        return (
            <FieldListEntry
                key={id}
                fieldKey={f.fieldkey}
                fields={fields}
                altTreeSearched={isSearchActive}
                TreeType={TreeType}
                showHiddenFields={showHiddenFields}
                ctrlShift={!editable && fieldFollowsThirdPartyRules ? false : ctrlShift}
                onCtrlShift={onCtrlShift}
                editable={editable}
                isGrayout={isGrayout}
                tabType={tabType}
                showTabs={showTabs}
                isTableActive={f.TableActive === "Y"}
            />
        );
    });

    return (
        <div style={{ paddingLeft: 20 }}>
            {folderElements}
            {fieldElements}
        </div>
    );
};

export default LazyFieldList;
